// 261023 E3-P3b2 — «Chấm lại bằng AI» MỘT tập: quyết định nút (hiện / nổi bật / khoá + lý do) và chữ. THUẦN (jest) — KHÔNG import antd / react.
//
// Một hàm cho cả hai chỗ đặt nút (hàng Sổ duyệt và thẻ giám khảo đầu tập ⑤) để hai nơi không lệch nhau.
// BE là cổng thật (409 trả chữ tiếng Việt); ở đây chỉ khoá TRƯỚC khi bấm những ca FE đã biết được.
import type { EpisodeScriptRecord } from '../../../types/adaptation';
import type { WorkbenchEpisode, WorkbenchView } from '../../../types/adaptationWorkbench';
import { friendlyApiError } from './jobErrorText';

export const REJUDGE_LABEL = 'Chấm lại bằng AI';
export const REJUDGE_TIP = 'Tốn AI, khoảng 30 giây';
export const REJUDGE_RUNNING_TEXT = 'Đang chấm lại… (~30 giây)';
export const REJUDGE_JOB_LABEL = 'Đang chấm lại bằng AI';
/** Phase của job nền chấm lại (BE `FILM_JOB.WRITE_SCRIPTS`, `detail.phase`). */
export const REJUDGE_PHASE = 'c2-rejudge';

const BUSY_TEXT = 'Đang có việc chạy nền — đợi xong rồi thử lại';
const RUNNING_TEXT = 'Đang chấm lại bằng AI — đợi xong';
const runningOtherText = (n?: number): string => `Đang chấm lại bằng AI${n === undefined ? '' : ` tập ${n}`} — đợi xong`;
const PLAN_TEXT = 'Dàn tập chưa chốt — chốt rồi mới chấm được';
const WRITER_UNKNOWN_TEXT = 'Không rõ model đã viết bản này (bản được khôi phục) — viết lại tập để chấm';
const NO_STAMP_TEXT = 'Chưa có mốc thời gian của bản kịch bản — tải lại rồi thử lại';
const FAILURE_FALLBACK = 'Không chấm lại được — thử lại sau';

/** Tập có người/trợ lý sửa tay hoặc khôi phục sau lần viết ⇒ nên chấm lại trước khi đẩy. */
const EDITED_ORIGINS: ReadonlySet<string> = new Set(['MANUAL_EDIT', 'CHAT_EDIT', 'RESTORE']);
/** Job nào khoá cả truyện với việc chấm lại: BE khoá theo `WRITE_SCRIPTS` của truyện (viết / sửa-nối qua trợ lý / đọc cả mùa). */
const BLOCKING_KINDS: ReadonlySet<string> = new Set(['write', 'other', 'season_reader']);

export const isRejudgeJob = (j: { phase?: string | null } | null | undefined): boolean => j?.phase === REJUDGE_PHASE;

export interface RejudgeAction {
  show: boolean;
  /** Nút nổi bật (tập đáng chấm lại hơn cả). */
  primary: boolean;
  disabled: boolean;
  /** Lý do khoá (null khi bấm được). */
  reason: string | null;
  /** Chữ tooltip: lý do khi khoá, giá khi bấm được. */
  tip: string;
  /** Job chấm lại đang chạy cho CHÍNH tập này (để hiện «Đang chấm lại… (~30 giây)»). Tập khác trong lúc đó chỉ bị khoá. */
  running: boolean;
}

export interface RejudgeActionInput {
  ep: Pick<WorkbenchEpisode, 'ep' | 'written' | 'lastOrigin' | 'rejudge'> | null | undefined;
  script: Pick<EpisodeScriptRecord, 'script' | 'criticFreshness'> & { modifiedAt?: string } | null | undefined;
  workbench: Pick<WorkbenchView, 'jobs' | 'episodePlan'> | null | undefined;
  /** Lý do khoá của chỗ gọi (chặng khoá, đang sửa tay…). */
  lockedReason?: string | null;
}

const statusOf = (script: RejudgeActionInput['script']): string | undefined => {
  const qr: any = script?.script?.qualityReview;
  return qr && typeof qr === 'object' && qr.version === 2 && qr.critic && typeof qr.critic === 'object' ? String(qr.critic.status ?? '') : undefined;
};

export const rejudgeActionOf = ({ ep, script, workbench, lockedReason }: RejudgeActionInput): RejudgeAction => {
  const hasScenes = Array.isArray(script?.script?.scenes) && script!.script!.scenes.length > 0;
  if (!ep || ep.written !== 'draft' || !hasScenes) return { show: false, primary: false, disabled: true, reason: null, tip: '', running: false };

  const freshness = script?.criticFreshness;
  const primary = statusOf(script) === 'UNCHECKED' || (!!freshness && freshness !== 'CURRENT') || (!!ep.lastOrigin && EDITED_ORIGINS.has(ep.lastOrigin));

  const running = workbench?.jobs?.running ?? [];
  const rejudging = running.filter((j) => isRejudgeJob(j));
  const own = rejudging.some((j) => j.episode === ep.ep);   // BE cũ không gửi `episode` ⇒ không biết của tập nào ⇒ chỉ khoá, không quay vòng
  const busy = running.some((j) => BLOCKING_KINDS.has(j.kind));
  let reason: string | null = null;
  if (own) reason = RUNNING_TEXT;
  else if (rejudging.length) reason = runningOtherText(rejudging[0].episode);
  else if (busy) reason = BUSY_TEXT;
  else if (lockedReason) reason = lockedReason;
  else if (workbench?.episodePlan && workbench.episodePlan.state !== 'APPROVED') reason = PLAN_TEXT;
  else if (ep.rejudge && ep.rejudge.available === false) reason = WRITER_UNKNOWN_TEXT;
  else if (!script?.modifiedAt) reason = NO_STAMP_TEXT;

  return { show: true, primary, disabled: reason !== null, reason, tip: reason ?? REJUDGE_TIP, running: own };
};

/**
 * Tooltip lý do khoá bọc cái gì: nút `disabled` KHÔNG nhận sự kiện chuột nên Tooltip bọc thẳng nút sẽ không bao giờ hiện (lỗi smoke 3b2).
 * Khoá ⇒ bọc `<span>`; bấm được ⇒ bọc thẳng nút (giữ DOM như cũ).
 */
export const rejudgeTipHost = (action: Pick<RejudgeAction, 'disabled'>): 'span' | 'button' => (action.disabled ? 'span' : 'button');

/** Toast khi bấm bị từ chối (409 …): chữ BE, không thử lại. */
export const rejudgeFailureToast = (e: unknown): string => friendlyApiError(e, FAILURE_FALLBACK);
